import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Field, Label, Switch } from "@headlessui/react";
import { useAppSelector } from "../../../../app/hooks";
import { Loading } from "../../../../components/Loading";
import { useAdminReportsFilters } from "../useAdminReportsFilters";
import { getReportLevel, buildReportQueryString, reportSubLevelName } from "../adminReportsUtil";
import { ReportLevel, AdminReportsResponse } from "../adminReportsApiSlice";
import { DistrictSchoolFilters } from "../DistrictSchoolFilters";
import { TableRightSideHeader } from "../TableRightSideHeader";
import { PeerReviewUsageRow, useLazyPeerReviewUsageQuery } from "./adminUsageReportApiSlice";
import { PeerReviewUsageTable } from "./PeerReviewUsageTable";
import { PeerReviewUsageChart } from "./PeerReviewUsageChart";

export default function PeerReviewUsage() {
	const { aspUrl } = useAppSelector((state) => state.site);
	const {
		maxReportLevel,
		districtOptions,
		schoolOptions,
		districtFilter,
		schoolFilter,
		setDistrictFilter,
		setSchoolFilter,
	} = useAdminReportsFilters();

	const navigate = useNavigate();

	const [data, setData] = useState<AdminReportsResponse<PeerReviewUsageRow>>();
	const [lastUpdated, setLastUpdated] = useState<Date>();
	const [hideRows, setHideRows] = useState(false);
	const [hideZeroRows, setHideZeroRows] = useState(false);
	const [reportLevel, setReportLevel] = useState<ReportLevel>(maxReportLevel);

	const [callPeerReviewUsage] = useLazyPeerReviewUsageQuery();

	useEffect(() => {
		// skip if don't have the necessary ids for the max level to prevent auto-fail calls
		if (maxReportLevel === ReportLevel.School && (!districtFilter || !schoolFilter)) {
			return;
		}
		if (maxReportLevel === ReportLevel.District && !districtFilter) {
			return;
		}

		callPeerReviewUsage(
			{
				districtId: districtFilter === 0 ? undefined : districtFilter,
				schoolId: schoolFilter === 0 ? undefined : schoolFilter,
			},
			true //cache the results
		)
			.unwrap()
			.then((payload) => {
				setData(payload.response);
				setLastUpdated(payload.lastUpdateDate);
				setReportLevel(getReportLevel(districtFilter, schoolFilter));
			})
			.catch((error) => {
				console.log(error.data);
				navigate("/adminReports");
			});
	}, [districtFilter, schoolFilter]);

	const queryString = useMemo(() => {
		return buildReportQueryString({
			districtFilter,
			schoolFilter,
		});
	}, [districtFilter, schoolFilter]);

	const filteredRows = useMemo(() => {
		if (!data) return [];
		if (!hideZeroRows) return data.rows;
		return data.rows.filter((row) => row.peerReviewRequestedCount > 0);
	}, [data, hideZeroRows]);

	return (
		<section>
			<h1>
				{reportLevel} Peer Review Usage by {reportSubLevelName(reportLevel)}
			</h1>
			<p className="mt-4 print:hidden">
				This report shows the number of requested and completed peer reviews for each{" "}
				{reportSubLevelName(reportLevel).toLowerCase()}. Use the filters below
				{reportLevel !== ReportLevel.School ? " or select the name in the table" : ""} to
				view data for that selection or hide rows with no requests. Click the three dots to
				print or download.
			</p>
			<div className="mt-3">
				<Loading loading={!data} />
				{data && (
					<>
						<div className="flex justify-between items-end gap-x-3">
							<div className="flex flex-wrap gap-3 items-end print:hidden">
								<DistrictSchoolFilters
									districtOptions={districtOptions}
									schoolOptions={schoolOptions}
									districtFilter={districtFilter}
									schoolFilter={schoolFilter}
									setDistrictFilter={setDistrictFilter}
									setSchoolFilter={setSchoolFilter}
								/>
								<Field className="w-full md:w-auto flex-none">
									<Label
										as="span"
										className="block mb-2 text-sm font-medium text-gray-90 pt-2 md:pt-0">
										Hide {reportSubLevelName(reportLevel)}s with No Requests
									</Label>
									<Switch
										checked={hideZeroRows}
										onChange={setHideZeroRows}
										name="hideZeroRows"
										id="hideZeroRows"
										className="group relative inline-flex h-8 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
										<span
											aria-hidden="true"
											className="pointer-events-none inline-block size-7 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-6"
										/>
									</Switch>
								</Field>
							</div>
							<TableRightSideHeader
								lastUpdated={lastUpdated}
								hasData={filteredRows.length !== 0}
								hideRows={hideRows}
								setHideRows={setHideRows}
								uniqueKey="peer-review-usage"
								downloadExcelLink={`${aspUrl}/AdminReports/PeerReviewUsageExcel?${queryString}`}
								downloadCsvLink={`${aspUrl}/AdminReports/PeerReviewUsageCsv?${queryString}`}
							/>
						</div>

						<PeerReviewUsageTable
							className="mt-4"
							level={reportLevel}
							fullData={data}
							filteredRows={filteredRows}
							hideRows={hideRows}
							setDistrictFilter={setDistrictFilter}
							setSchoolFilter={setSchoolFilter}
						/>
						<div className="print:hidden">
							<PeerReviewUsageChart
								level={reportLevel}
								data={filteredRows}
								hasData={filteredRows.length > 0}
							/>
						</div>
					</>
				)}
			</div>
		</section>
	);
}
